import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { getPublicSettings } from '../services/SettingsService';
import { SITE_CONFIG } from '../config';

/**
 * Component to apply SEO settings to the HTML head
 *
 * @param {Object} props - Component props
 * @param {string} props.title - Page title (will be appended to site title)
 * @param {string} props.description - Page description (overrides default)
 * @param {string} props.keywords - Page keywords (overrides default)
 * @param {string} props.ogImage - Open Graph image URL (overrides default)
 * @returns {JSX.Element} Helmet component with SEO tags
 */
const SeoHead = ({ title, description, keywords, ogImage }) => {
  const [seoSettings, setSeoSettings] = useState({
    title: SITE_CONFIG.NAME,
    description: 'Marketplace do kupna i sprzedaży — wystaw ogłoszenie lub znajdź okazję na 3Bay.',
    keywords: '3bay, ogłoszenia, kupno, sprzedaż, marketplace',
    author: '3Bay Team',
    ogImage: '/images/og-image.jpg',
    favicon: '/favicon.ico'
  });
  const [loading, setLoading] = useState(true);

  // Load SEO settings on component mount
  useEffect(() => {
    const loadSeoSettings = async () => {
      try {
        const { data: publicSettings, meta } = await getPublicSettings();
        if (meta) {
          console.warn('Public settings warning:', meta);
        }
        if (publicSettings && publicSettings.seo) {
          setSeoSettings((prev) => ({
            ...prev,
            ...publicSettings.seo
          }));
        }
      } catch (error) {
        console.error('Failed to load SEO settings:', error);
        // Continue with default settings
      } finally {
        setLoading(false);
      }
    };

    // Set a timeout to prevent blocking the UI
    const timer = setTimeout(() => {
      loadSeoSettings();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Combine props with default settings
  const pageTitle = title ? `${title} | ${seoSettings.title}` : seoSettings.title;
  const pageDescription = description || seoSettings.description;
  const pageKeywords = keywords || seoSettings.keywords;
  const pageOgImage = ogImage || seoSettings.ogImage;

  if (loading) {
    return null;
  }

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <meta name="author" content={seoSettings.author} />

      {/* Favicon */}
      <link rel="icon" href={seoSettings.favicon} />
      <link rel="apple-touch-icon" href={seoSettings.favicon} />

      {/* Open Graph / Social Media */}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageOgImage} />
      <meta property="og:type" content="website" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageOgImage} />
    </Helmet>
  );
};

export default SeoHead;
